<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0"
  >
    <div class="p-4 budget-main-layout">
      <div class="budget-left-panel">
        <div v-if="hasBudget" class="budget-chart-wrap">
          <div class="budget-chart-box">
            <Doughnut :data="chartData" :options="chartOptions" />
          </div>

          <div class="budget-summary-card">
            <div class="summary-card-section">
              <div class="summary-row">
                <span class="summary-dot" style="background:#e8a23a;"></span>
                <div>
                  <div class="summary-label">Living Arrangements (310~330)</div>
                  <div class="summary-value">
                    {{ formatCurrency(livingOld) }} / {{ formatCurrency(livingNew) }}
                  </div>
                </div>
              </div>

              <div class="summary-row">
                <span class="summary-dot" style="background:#84d37b;"></span>
                <div>
                  <div class="summary-label">
                    Employment &amp; Community Participation (331~355)
                  </div>
                  <div class="summary-value">
                    {{ formatCurrency(communityOld) }} / {{ formatCurrency(communityNew) }}
                  </div>
                </div>
              </div>

              <div class="summary-row">
                <span class="summary-dot" style="background:#d9534f;"></span>
                <div>
                  <div class="summary-label">Health &amp; Safety (356~399)</div>
                  <div class="summary-value">
                    {{ formatCurrency(healthOld) }} / {{ formatCurrency(healthNew) }}
                  </div>
                </div>
              </div>

              <div class="summary-row">
                <span class="summary-dot" style="background:#56e37c;"></span>
                <div>
                  <div class="summary-label">Available Budget</div>
                  <div class="summary-value">
                    {{ formatCurrency(availableBudget) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-meta">
              <div><strong>Total Budget:</strong> {{ formatCurrency(totalBudget) }}</div>
              <div><strong>Total Spent:</strong> {{ formatCurrency(totalExpenses) }}</div>
              <div><strong>Date From:</strong> {{ dateFrom }}</div>
              <div><strong>Date To:</strong> {{ dateTo }}</div>
            </div>

            <button
              v-if="hasBudget"
              @click="showReceipt = true"
              class="mt-4 w-full bg-blueGray-700 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
            >
              Domain Breakdown
            </button>
          </div>
        </div>

        <div v-else class="empty-state-card">
          No budget data available.
        </div>
      </div>

      <div class="budget-right-panel">
        <div class="spending-plan-card">
          <div class="spending-plan-header">
            <h3 class="spending-plan-title">Spending Plan Files</h3>
            <p class="spending-plan-subtitle">
              View uploaded spending plan documents.
            </p>
          </div>

          <div
            v-if="spendingPlanFiles.length === 0"
            class="spending-plan-empty"
          >
            No spending plans uploaded yet.
          </div>

          <div v-else class="spending-plan-list">
            <div
              v-for="file in spendingPlanFiles"
              :key="file.id"
              class="spending-plan-file-card"
            >
              <div class="spending-plan-file-left">
                <div class="file-icon-box">
                  <i class="fas fa-file-pdf"></i>
                </div>

                <div class="file-meta">
                  <div class="file-name">
                    {{ file.original_name }}
                  </div>
                  <div class="file-uploaded">
                    Uploaded: {{ file.uploaded_at }}
                  </div>
                </div>
              </div>

              <div class="spending-plan-file-actions">
                <button
                  @click="previewSpendingPlan(file.file_url, file.original_name)"
                  class="file-action-btn file-action-view"
                  title="Preview"
                >
                  <i class="fas fa-eye mr-1"></i> View
                </button>

                <button
                  @click="downloadSpendingPlan(file.file_url, file.original_name)"
                  class="file-action-btn file-action-download"
                  title="Download"
                >
                  <i class="fas fa-download mr-1"></i> Download
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showReceipt"
      class="receipt-overlay"
      @click.self="showReceipt = false"
    >
      <div class="receipt-panel">
        <div class="receipt-header">
          <span class="receipt-title">SPENDING PLAN DOMAINS</span>
          <button class="receipt-close" @click="showReceipt = false">
            &times;
          </button>
        </div>

        <div class="receipt-date">
          {{ dateFrom }} &mdash; {{ dateTo }}
        </div>

        <div class="receipt-divider"></div>

        <div class="receipt-row">
          <span class="receipt-vendor-name">Living Arrangements (310~330)</span>
          <span class="receipt-dots"></span>
          <span class="receipt-amount">
            {{ formatCurrency(livingOld) }} / {{ formatCurrency(livingNew) }}
          </span>
        </div>

        <div class="receipt-row">
          <span class="receipt-vendor-name">
            Employment &amp; Community Participation (331~355)
          </span>
          <span class="receipt-dots"></span>
          <span class="receipt-amount">
            {{ formatCurrency(communityOld) }} / {{ formatCurrency(communityNew) }}
          </span>
        </div>

        <div class="receipt-row">
          <span class="receipt-vendor-name">Health &amp; Safety (356~399)</span>
          <span class="receipt-dots"></span>
          <span class="receipt-amount">
            {{ formatCurrency(healthOld) }} / {{ formatCurrency(healthNew) }}
          </span>
        </div>

        <div class="receipt-row">
          <span class="receipt-vendor-name">Available Budget</span>
          <span class="receipt-dots"></span>
          <span class="receipt-amount">
            {{ formatCurrency(availableBudget) }}
          </span>
        </div>

        <div class="receipt-divider-double"></div>

        <div class="receipt-row receipt-total-row">
          <span class="receipt-vendor-name">TOTAL SPENT</span>
          <span class="receipt-dots"></span>
          <span class="receipt-amount">
            {{ formatCurrency(totalExpenses) }}
          </span>
        </div>

        <div class="receipt-divider"></div>

        <div class="receipt-row receipt-budget-row">
          <span class="receipt-vendor-name">BUDGET</span>
          <span class="receipt-dots"></span>
          <span class="receipt-amount">{{ formatCurrency(totalBudget) }}</span>
        </div>

        <div class="receipt-footer">Thank you</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from "chart.js";
import { Doughnut } from "vue-chartjs";
import axios from "axios";
import Swal from "sweetalert2";

ChartJS.register(Title, Tooltip, Legend, ArcElement);

export default {
  name: "BudgetMonitoring",
  components: { Doughnut },
  data() {
    return {
      totalBudget: 0,
      totalExpenses: 0,
      availableBudget: 0,
      livingOld: 0,
      livingNew: 0,
      communityOld: 0,
      communityNew: 0,
      healthOld: 0,
      healthNew: 0,
      hasBudget: false,
      dateFrom: "",
      dateTo: "",
      domainBreakdown: [],
      showReceipt: false,
      spendingPlanFiles: [],
    };
  },
  computed: {
    chartData() {
      return {
        labels: [
          "Living Arrangements",
          "Employment & Community Participation",
          "Health & Safety",
          "Available Budget",
        ],
        datasets: [
          {
            label: "Budget Distribution",
            data: [
              Number(this.livingOld) || 0,
              Number(this.communityOld) || 0,
              Number(this.healthOld) || 0,
              Number(this.availableBudget) || 0,
            ],
            backgroundColor: [
              "#e8a23a",
              "#84d37b",
              "#d9534f",
              "#56e37c",
            ],
            borderWidth: 1,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
          },
          title: {
            display: true,
            text: "Budget Distribution",
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = context.raw || 0;
                return `${context.label}: ${this.formatCurrency(value)}`;
              },
            },
          },
        },
      };
    },
  },
  methods: {
    async fetchClientBudget() {
      const token = localStorage.getItem("token");

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch-client-budget-data`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const budget = response?.data?.client_budget;

        if (budget) {
          this.totalBudget = Number(budget.total_budget) || 0;
          this.totalExpenses = Number(budget.spent_budget) || 0;
          this.availableBudget =
            Number(budget.available_budget ?? budget.remaining_budget) || 0;

          this.livingOld = Number(budget.living_old) || 0;
          this.livingNew = Number(budget.living_new) || 0;

          this.communityOld = Number(budget.community_old) || 0;
          this.communityNew = Number(budget.community_new) || 0;

          this.healthOld = Number(budget.health_old) || 0;
          this.healthNew = Number(budget.health_new) || 0;

          this.dateFrom = budget.from || "";
          this.dateTo = budget.to || "";
          this.domainBreakdown = Array.isArray(budget.domain_breakdown)
            ? budget.domain_breakdown
            : [];

          this.hasBudget = this.totalBudget > 0;
        } else {
          this.resetBudgetData();
        }
      } catch (error) {
        console.error("Backend error:", error);
        this.resetBudgetData();
      }
    },

    resetBudgetData() {
      this.totalBudget = 0;
      this.totalExpenses = 0;
      this.availableBudget = 0;
      this.livingOld = 0;
      this.livingNew = 0;
      this.communityOld = 0;
      this.communityNew = 0;
      this.healthOld = 0;
      this.healthNew = 0;
      this.dateFrom = "";
      this.dateTo = "";
      this.domainBreakdown = [];
      this.hasBudget = false;
    },

    async fetchSpendingPlanFiles() {
      const token = localStorage.getItem("token");

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch_spending_plan_files`,
          { token },
          { headers: { "Content-Type": "application/json" } }
        );

        this.spendingPlanFiles = response?.data?.data || [];
      } catch (err) {
        console.error("Failed to fetch spending plan files:", err);
      }
    },

    previewSpendingPlan(url, name) {
      Swal.fire({
        title: name,
        html: `<iframe src="${url}" style="width:100%;height:70vh;border:none;"></iframe>`,
        width: "80%",
        showConfirmButton: false,
        showCloseButton: true,
      });
    },

    downloadSpendingPlan(url, name) {
      fetch(url)
        .then((resp) => resp.blob())
        .then((blob) => {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = name || "spending_plan.pdf";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        })
        .catch((err) => console.error("Download error:", err));
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return "-";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(Number(value) || 0);
    },
  },
  mounted() {
    this.fetchClientBudget();
    this.fetchSpendingPlanFiles();
  },
};
</script>

<style scoped>
.budget-main-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  gap: 20px;
  align-items: start;
}

.budget-left-panel,
.budget-right-panel {
  min-width: 0;
}

.budget-chart-wrap {
  display: grid;
  grid-template-columns: minmax(340px, 1fr) minmax(300px, 360px);
  gap: 20px;
  align-items: stretch;
}

.budget-chart-box {
  background: #ffffff;
  border: 1px solid #d7dee8;
  border-radius: 16px;
  padding: 20px;
  min-height: 460px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.budget-summary-card {
  background: #ffffff;
  border: 1px solid #d7dee8;
  border-radius: 16px;
  padding: 20px;
  min-height: 460px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
}

.summary-card-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.summary-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.summary-dot {
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  margin-top: 6px;
  flex-shrink: 0;
}

.summary-label {
  font-size: 0.95rem;
  font-weight: 700;
  color: #24324a;
  line-height: 1.35;
}

.summary-value {
  font-size: 0.95rem;
  color: #334155;
  margin-top: 2px;
  font-weight: 600;
}

.summary-divider {
  border-top: 1px solid #e5e7eb;
  margin: 18px 0 14px;
}

.summary-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.92rem;
  color: #334155;
}

.empty-state-card {
  background: #ffffff;
  border: 1px solid #d7dee8;
  border-radius: 16px;
  padding: 40px 24px;
  min-height: 260px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-weight: 600;
}

.spending-plan-card {
  background: #ffffff;
  border: 1px solid #d7dee8;
  border-radius: 16px;
  padding: 20px;
  min-height: 460px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
}

.spending-plan-header {
  margin-bottom: 18px;
}

.spending-plan-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #24324a;
  margin-bottom: 4px;
}

.spending-plan-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}

.spending-plan-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 0.95rem;
  border: 1px dashed #d7dee8;
  border-radius: 14px;
  min-height: 220px;
}

.spending-plan-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.spending-plan-file-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px 16px;
  background: #f8fafc;
}

.spending-plan-file-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.file-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fee2e2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.file-meta {
  min-width: 0;
}

.file-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  word-break: break-word;
}

.file-uploaded {
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 4px;
}

.spending-plan-file-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.file-action-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
}

.file-action-view {
  background: #3b82f6;
}

.file-action-download {
  background: #10b981;
}

.receipt-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.receipt-panel {
  background: #fffef5;
  border: 1px solid #d4d0c8;
  border-radius: 4px;
  padding: 24px 28px;
  min-width: 320px;
  max-width: 460px;
  width: 92%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  font-family: "Courier New", Courier, monospace;
  font-size: 13px;
  color: #333;
}

.receipt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.receipt-title {
  font-weight: bold;
  font-size: 15px;
  letter-spacing: 2px;
}

.receipt-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #999;
  line-height: 1;
}

.receipt-close:hover {
  color: #333;
}

.receipt-date {
  text-align: center;
  font-size: 11px;
  color: #888;
  margin-bottom: 8px;
}

.receipt-divider {
  border-bottom: 1px dashed #bdbdbd;
  margin: 10px 0;
}

.receipt-divider-double {
  border-bottom: 2px double #888;
  margin: 12px 0;
}

.receipt-row {
  display: flex;
  align-items: center;
  font-size: 13px;
  margin-bottom: 4px;
}

.receipt-vendor-name {
  white-space: nowrap;
  font-weight: 500;
}

.receipt-dots {
  flex: 1;
  border-bottom: 1px dotted #bbb;
  margin: 0 8px;
  height: 1em;
}

.receipt-amount {
  min-width: 130px;
  text-align: right;
  font-weight: 600;
}

.receipt-total-row .receipt-vendor-name,
.receipt-total-row .receipt-amount {
  font-weight: bold;
  color: #1976d2;
}

.receipt-budget-row .receipt-vendor-name,
.receipt-budget-row .receipt-amount {
  color: #444;
}

.receipt-footer {
  text-align: center;
  margin-top: 18px;
  font-size: 11px;
  color: #aaa;
  letter-spacing: 1px;
}

@media (max-width: 1200px) {
  .budget-main-layout {
    grid-template-columns: 1fr;
  }

  .budget-chart-wrap {
    grid-template-columns: 1fr;
  }

  .budget-chart-box,
  .budget-summary-card,
  .spending-plan-card {
    min-height: auto;
  }
}

@media (max-width: 768px) {
  .spending-plan-file-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .spending-plan-file-actions {
    width: 100%;
  }

  .file-action-btn {
    flex: 1;
    width: 100%;
  }

  .budget-chart-box {
    min-height: 360px;
  }
}

@media (max-width: 767px) {
  .budget-main-layout {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .budget-chart-wrap {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .budget-chart-box,
  .budget-summary-card,
  .spending-plan-card,
  .empty-state-card {
    min-height: auto;
    padding: 14px;
    border-radius: 14px;
  }

  .budget-chart-box {
    min-height: 300px;
  }

  .summary-card-section {
    gap: 12px;
  }

  .summary-row {
    gap: 8px;
  }

  .summary-label {
    font-size: 0.84rem;
    line-height: 1.3;
    word-break: break-word;
  }

  .summary-value {
    font-size: 0.88rem;
    line-height: 1.3;
    word-break: break-word;
  }

  .summary-meta {
    gap: 6px;
    font-size: 0.84rem;
  }

  .spending-plan-header {
    margin-bottom: 14px;
  }

  .spending-plan-title {
    font-size: 0.98rem;
  }

  .spending-plan-subtitle {
    font-size: 0.82rem;
  }

  .spending-plan-file-card {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 12px;
    border-radius: 12px;
  }

  .spending-plan-file-left {
    align-items: flex-start;
    gap: 10px;
  }

  .file-icon-box {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    font-size: 0.95rem;
  }

  .file-name {
    font-size: 0.88rem;
    line-height: 1.3;
  }

  .file-uploaded {
    font-size: 0.78rem;
  }

  .spending-plan-file-actions {
    width: 100%;
    flex-direction: column;
    gap: 8px;
  }

  .file-action-btn {
    width: 100%;
    padding: 10px 12px;
    font-size: 0.78rem;
    text-align: center;
  }

  .receipt-panel {
    min-width: 0;
    width: 94%;
    max-width: 94%;
    padding: 18px 16px;
  }

  .receipt-row {
    font-size: 12px;
    align-items: flex-start;
  }

  .receipt-vendor-name {
    white-space: normal;
    max-width: 46%;
    line-height: 1.3;
  }

  .receipt-amount {
    min-width: 88px;
    font-size: 12px;
  }
}

</style>
